//----------------CORE-----------------//
import React, { useEffect } from "react";
import { makeStyles } from "@mui/styles";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
//--------------MUI-------------//
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Typography from "@mui/material/Typography";
import { Grid } from "@mui/material";
import WebIcon from "@mui/icons-material/Web";
import { useGetTagsQuery } from "services/tags";
import Grow from "@mui/material/Grow";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
//-----------EXTERNAL--------//
import Media from "components/Card Skeleton";
import TagFilter, { ActiveTagFilter, SolutionTagChips } from "./tags";
import SolutionSearch from "./search";
import NoImage from "assets/img/catalogue.jpg";
import { setSelectedTags } from "rtkSlices/selectedTagsSlice";
import { setServices } from "rtkSlices/metaDataSlice";
import { useUploadBrandingMutation } from "services/branding";
import { resetFilter, setFilter } from "rtkSlices/filterDevicesSlice";
import {
  tagNameMap,
  solutionTags,
  tagsInUse,
  filterSolutions,
} from "utils/solutionTags";

import Keys from "Keys";
import "./catalogue.css";

let rendered = false;
// D30 (3): the tag filter's match mode lives as long as the tag selection (redux, one page
// load), so coming back to the page keeps both.
let tagMatchMode = "any";

export default function MediaCard(props) {
  let token = window.localStorage.getItem("token");
  const dispatch = useDispatch();
  const [uploadBranding, uploadResult] = useUploadBrandingMutation();
  const [search, setSearch] = React.useState("");
  const [tagMode, setTagMode] = React.useState(tagMatchMode);
  const metaDataValue = useSelector((state) => state.metaData);
  const [hovered, setHovered] = React.useState(false);
  const selectedTags = useSelector((state) => {
    if (state.selectedTags?.value) return state.selectedTags.value;
    else return state.selectedTags.length ? state.selectedTags : [];
  });
  const { enqueueSnackbar } = useSnackbar();
  // const [pinId, setPinId] = React.useState('')

  const useStyles = makeStyles({
    root: {
      transition: "0.3s",
      "&:hover": {
        transition: "0.3s",
        transform: "translate(0, -5px)",
      },
    },
    card: {
      transition: "0.3s",
      width: "100%",
      "&:hover": {
        transition: "0.3s",
        boxShadow: "rgb(38, 57, 77) 0px 20px 30px -10px",
        transform: "translate(0, -5px)",
      },
    },
    media: {
      height: 200,
    },
    accordionSummary: {
      "> .MuiAccordionSummary-content": {
        display: "contents",
      },
    },
  });
  const classes = useStyles();
  const tagsRes = useGetTagsQuery();
  // D30 (3): this page gets tag IDS on each solution and the names from the tag read, which
  // answers 400 for a tenant with no tags - no names then, so no tags to show or filter by.
  const tagNames = tagNameMap(tagsRes.data?.payload);
  const tagsOf = (s) => solutionTags(s.tags, tagNames);
  const tagsUsed = tagsInUse(metaDataValue.services, tagsOf);
  // A remembered tag that none of these solutions carries any more is left out.
  const selectedTagIds = selectedTags
    .map((t) => String(t._id))
    .filter((id) => tagsUsed.some((t) => t.id === id));
  // D30 (2)/(3): ONE pass over the solutions - the search (name, description, tag names) and
  // the tag filter (any/all) - so a solution is listed once however many selected tags it
  // carries (the old per-tag concatenation showed it once per tag: the duplicate-card bug).
  const services = filterSolutions(metaDataValue.services, {
    query: search,
    selectedIds: selectedTagIds,
    mode: tagMode,
    tagsOf,
  });
  // const [page, setPage] = useState(0);
  useEffect(() => {
    if (!rendered) {
      rendered = true;
    }
  });

  useEffect(() => {
    // document.title = "Solution Catalogue";
    // console.log('hereeeee')
    window.localStorage.setItem("currentService", "");
    dispatch(setFilter({ searching: false, search: "", searchFields: [], expanded: ["0:All assets"], group: { name: "All assets", id: "" } }))
  }, []);

  function handleClick(path) {
    const kpiData = localStorage.getItem("kpiData") ? JSON.parse(localStorage.getItem("kpiData")) : [];
    const kpiService = kpiData.find(k => k?.serviceId == path);
    const hasMatchingService = metaDataValue.services.some(service => service.id === kpiService?.serviceId && service?.energySavingEnable);

    if (kpiData.length && kpiData.find(k => k.serviceId == path) && kpiData.find(k => k.serviceId == path).popup) {
      props.history.push(`/solutions/${path}/kpi`);
    }else if (kpiData.length && kpiData.find(k => k.serviceId == path) && kpiData.find(k => k.serviceId == path).videoWallPopup) {
      props.history.push(`/solutions/${path}/video_wall`);
    }else if (kpiData.length && kpiData.find(k => k.serviceId == path) && kpiData.find(k => k.serviceId == path).energySavingAQIPopup && hasMatchingService) {
      props.history.push(`/solutions/${path}/energy_saving`);
    }
    else {
      props.history.push(`/solutions/${path}`);
    }
  }

  // D30 (3): the tag selection stays in redux as { _id, name } (as before); the mode beside it.
  function setTagFilter(ids, mode) {
    tagMatchMode = mode;
    setTagMode(mode);
    dispatch(
      setSelectedTags(ids.map((id) => ({ _id: id, name: tagNames[id] })))
    );
  }

  function handleTagDelete(id) {
    setTagFilter(
      selectedTagIds.filter((s) => s != id),
      tagMode
    );
  }

  // D30 (3): clicking a tag chip on a card adds that tag to the filter.
  function addTagToFilter(id) {
    if (!selectedTagIds.includes(id)) setTagFilter([...selectedTagIds, id], tagMode);
  }

  function getServices() {
    return services;
  }

  const onPin = async (id) => {
    let tempServices = JSON.parse(JSON.stringify(metaDataValue.services));
    let index = tempServices.findIndex((t) => t.id == getServices()[id - 1].id);
    let pinnedIndex = tempServices.filter((t) => t.pinned).length + 1;
    if (tempServices[index].pinned) {
      delete tempServices[index].pinned;
    } else {
      tempServices[index].pinned = pinnedIndex;
    }
    let pinnedSolutions = [];
    tempServices
      .filter((t) => t.pinned)
      .forEach((p) => {
        pinnedSolutions.push({ solution: p.id, pin: p.pinned });
      });
    let body = {
      pinnedSolutions,
    };
    let updatedSettings = await uploadBranding({
      token,
      body,
      type: metaDataValue.settings ? "PUT" : "POST",
      user: "true",
    });
    if (updatedSettings.data?.success) {
      dispatch(setServices(tempServices));
    } else if (updatedSettings.error) {
      showSnackbar(
        "Settings",
        updatedSettings.error.data?.message,
        "error",
        1000
      );
    }
    dispatch(setServices(tempServices));
  };

  function showSnackbar(title, message, variant, timeOut) {
    return enqueueSnackbar({ title, message, variant }, { timeOut });
  }

  return (
    <div
      id="sol-catalogue"
      style={{
        height: "calc(100vh - 110px)",
        overflowY: "scroll",
        scrollbarWidth: "none",
        padding: "10px",
      }}
    >
      <div>
        <ActiveTagFilter
          tags={tagsUsed.filter((t) => selectedTagIds.includes(t.id))}
          mode={tagMode}
          onRemove={handleTagDelete}
        />
        {getServices().length ? (
          <Grid container spacing={2}>
            {getServices().map((elm, i) => (
              <Grid item xs={12} sm={6} md={4} id={`solution-${elm.id}`}>
                <Grow appear={!rendered} in timeout={(i + 1) * 200}>
                  <div className={classes.root}>
                    <Card
                      onClick={(e) => {
                        if (
                          e.target?.viewportElement?.id == "pin-icon" ||
                          e.target.id == "pin-icon"
                        ) {
                          return;
                        }
                        handleClick(elm.path);
                      }}
                      onMouseOver={() => setHovered(i + 1)}
                      onMouseLeave={() => setHovered(false)}
                      className={classes.card}
                    >
                      <CardActionArea>
                        <CardMedia
                          className={classes.media}
                          image={elm.image ? elm.image : NoImage}
                        />
                        <CardContent
                          style={{
                            minHeight: "102px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography
                              gutterBottom
                              variant="h7"
                              component="strong"
                            >
                              {elm.name}
                            </Typography>
                            {hovered == i + 1 ||
                              metaDataValue.services.find(
                                (m, ind) => m.pinned && ind == i
                              ) ? (
                              <PushPinOutlinedIcon
                                fontSize="small"
                                color={
                                  metaDataValue.services.find(
                                    (m, ind) => m.pinned && ind == i
                                  )
                                    ? "primary"
                                    : ""
                                }
                                onClick={() => onPin(i + 1)}
                                id="pin-icon"
                              />
                            ) : null}
                          </div>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            component="p"
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              marginBottom: "2px",
                            }}
                          >
                            {elm.description}
                          </Typography>
                          <SolutionTagChips
                            tags={tagsOf(elm)}
                            onTagClick={addTagToFilter}
                          />
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </div>
                </Grow>
              </Grid>
            ))}
          </Grid>
        ) : (
          <div
            style={{
              textAlign: "center",
              fontSize: 35,
              fontWeight: 500,
              marginTop: "14%",
              color: "rgb(199, 199, 199)",
            }}
          >
            <WebIcon style={{ color: "#c7c7c7", fontSize: "100px" }} />
            <div style={{ color: "#c8c8c8" }}>
              <p style={{ fontSize: "20px", marginTop: "10px" }}>
                {selectedTags.length || search
                  ? "No such solution found. Please adjust filters"
                  : "No Solutions added. Please consult platform administrator to add solutions here"}
              </p>
            </div>
          </div>
        )}
        <div id="fab-container" >
          <SolutionSearch
            value={search}
            onChange={setSearch}
            name="catalogue-search"
          />
          <TagFilter
            tags={tagsUsed}
            selected={selectedTagIds}
            mode={tagMode}
            onApply={({ selected, mode }) => setTagFilter(selected, mode)}
            name="catalogue-filter"
          />
        </div>
      </div>
    </div>
  );
}
